import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/modules/shadcn/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/modules/shadcn/ui/tabs";
import { useTranslation } from "react-i18next";

import { AnnotationTypeSelector } from "./AnnotationTypeSelector";
import { ConfidenceReportSection } from "./ConfidenceReportSection";
import { EmptyState } from "./EmptyState";
import { InstancesChart } from "./InstancesChart";
import { SizesChart } from "./SizesChart";
import { useDatasetStats } from "./useDatasetStats";

export interface AnalyticsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AnalyticsDialog = ({
  open,
  onOpenChange,
}: AnalyticsDialogProps) => {
  const { t } = useTranslation(["analytics", "common"]);
  const {
    data,
    isLoading,
    isError,
    requestTypes,
    toggleType,
    hasGeometricType,
    hasAnyAreaData,
  } = useDatasetStats(open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-4 sm:max-w-215">
        <DialogHeader>
          <DialogTitle>{t("dialog.title")}</DialogTitle>
        </DialogHeader>
        {/* Charts */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
          <DialogTitle>{t("dialog.statsTitle")}</DialogTitle>

          {/* Annotation type selector */}
          <AnnotationTypeSelector
            availableTypes={data?.availableTypes}
            selectedTypes={requestTypes}
            onToggle={toggleType}
          />
          {isLoading && (
            <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
              {t("dialog.loading")}
            </div>
          )}
          {isError && (
            <div className="flex h-64 items-center justify-center text-sm text-red-500">
              {t("dialog.loadFailed")}
            </div>
          )}
          {data && !isLoading && !isError && (
            <Tabs defaultValue="instances">
              <TabsList variant="line" className="shrink-0">
                <TabsTrigger value="instances">{t("dialog.tabs.instances")}</TabsTrigger>
                <TabsTrigger value="sizes">{t("dialog.tabs.sizes")}</TabsTrigger>
              </TabsList>

              <TabsContent value="instances" className="pt-4">
                {data.labels.length === 0 ? (
                  <EmptyState message={t("dialog.noLabels")} />
                ) : (
                  <InstancesChart labels={data.labels} />
                )}
              </TabsContent>

              <TabsContent value="sizes" className="pt-4">
                {!hasGeometricType ? (
                  <EmptyState message={t("dialog.selectGeometric")} />
                ) : !hasAnyAreaData ? (
                  <EmptyState message={t("dialog.noGeometric")} />
                ) : (
                  <SizesChart labels={data.labels} />
                )}
              </TabsContent>
            </Tabs>
          )}

          {/* Confidence Report section — always visible regardless of stats loading state */}
          <ConfidenceReportSection />
        </div>
      </DialogContent>
    </Dialog>
  );
};
